import { useState, useEffect } from 'react';
import * as profileListingService from '../../../services/profileListingService';
import type { ProfileListingModuleData, ProfileListingItem } from '../../../services/profileListingService';

interface ProfileBusinessListingDetailProps {
  onBack: () => void;
  darkMode: boolean;
}

interface NapIssue {
  id: string;
  directory: string;
  field: string;
  current: string;
  expected: string;
  severity: 'critical' | 'warning' | 'info';
}

export default function ProfileBusinessListingDetail({ onBack, darkMode }: ProfileBusinessListingDetailProps) {
  const [activeTab, setActiveTab] = useState<'new' | 'existing'>('new');
  const [napAuditRun, setNapAuditRun] = useState(false);

  // API State
  const [moduleData, setModuleData] = useState<ProfileListingModuleData | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [generatedListings, setGeneratedListings] = useState<ProfileListingItem[]>([]);
  const [profileData, setProfileData] = useState<ProfileListingItem | null>(null);
  const [listingsGenerated, setListingsGenerated] = useState(false);

  // Scan state
  const [isScanning, setIsScanning] = useState(false);
  const [scanId, setScanId] = useState<string | null>(null);
  const [scanAssets, setScanAssets] = useState<any[]>([]);

  // Drawer state
  const [drawerListing, setDrawerListing] = useState<ProfileListingItem | null>(null);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('view');
  const [isCopied, setIsCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editDesc, setEditDesc] = useState('');
  const [editBusinessName, setEditBusinessName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editWebsite, setEditWebsite] = useState('');
  const [editCategory, setEditCategory] = useState('');

  // Asset Checklist State
  const [checkedPlatforms, setCheckedPlatforms] = useState<Record<string, boolean>>({});
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteSuccess, setMarkCompleteSuccess] = useState(false);

  // Sync profileData with generatedListings
  useEffect(() => {
    if (generatedListings.length > 0) {
      const valid = generatedListings.find(l => l.status !== 'FAILED');
      setProfileData(valid || generatedListings[0]);
    } else {
      setProfileData(null);
    }
  }, [generatedListings]);

  // Sync checkedPlatforms state with profileData's published_platforms
  useEffect(() => {
    if (profileData && moduleData?.platforms) {
      const initialChecked: Record<string, boolean> = {};
      const published = profileData.published_platforms || [];
      moduleData.platforms.forEach(p => {
        initialChecked[p.platform_name] = published.includes(p.platform_name);
      });
      setCheckedPlatforms(initialChecked);
    } else {
      setCheckedPlatforms({});
    }
  }, [profileData, moduleData]);

  const handleTogglePlatformCheckbox = (platformName: string) => {
    setCheckedPlatforms(prev => ({
      ...prev,
      [platformName]: !prev[platformName]
    }));
  };

  const handleMarkCompletePerListing = async () => {
    if (!profileData) return;
    setIsMarkingComplete(true);
    try {
      const selectedPlatforms = Object.keys(checkedPlatforms).filter(p => checkedPlatforms[p]);
      const updated = await profileListingService.updateListing(profileData.id, {
        published_platforms: selectedPlatforms
      });
      
      setGeneratedListings(prev => prev.map(l => l.id === updated.id ? updated : l));
      setProfileData(updated);
      setMarkCompleteSuccess(true);
      setTimeout(() => setMarkCompleteSuccess(false), 3000);
    } catch (err) {
      console.error('Error marking complete for listing:', err);
    } finally {
      setIsMarkingComplete(false);
    }
  };


  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const accentBg = darkMode ? 'bg-blue-900/30' : 'bg-blue-50';
  const accentBorder = darkMode ? 'border-blue-800' : 'border-blue-200';

  useEffect(() => {
    async function loadData() {
      try {
        const data = await profileListingService.getModuleData();
        setModuleData(data);
        if (data.is_new_website) {
          setActiveTab('new');
        } else {
          setActiveTab('existing');
        }
        
        // Also fetch any previously generated listings
        const existingListings = await profileListingService.getAllListings();
        if (existingListings.length > 0) {
          setGeneratedListings(existingListings);
          setListingsGenerated(true);
          setNapAuditRun(true);
        }
      } catch (err) {
        console.error('Failed to load profile listing module data', err);
      } finally {
        setLoadingData(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    let interval: number | undefined;
    if (generationId && isGenerating) {
      interval = window.setInterval(async () => {
        try {
          const items = await profileListingService.getGeneratedListings(generationId);
          setGeneratedListings(items);
          const allDone = items.length > 0 && items.every(item => item.status !== 'GENERATING');
          if (allDone) {
            setIsGenerating(false);
            setListingsGenerated(true);
            setNapAuditRun(true);
          }
        } catch (err) {
          console.error('Error polling listings', err);
        }
      }, 3000);
    }
    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [generationId, isGenerating]);

  // Polling for Scan
  useEffect(() => {
    let interval: number | undefined;
    if (scanId && isScanning) {
      interval = window.setInterval(async () => {
        try {
          const res = await profileListingService.getScanStatus(scanId);
          if (res.status === 'COMPLETED') {
            setIsScanning(false);
            setScanAssets(res.result?.assets || []);
            setNapAuditRun(true);
          } else if (res.status === 'FAILED') {
            setIsScanning(false);
            setNapAuditRun(true);
            setScanAssets([]);
          }
        } catch (err) {
          console.error('Error polling scan status', err);
        }
      }, 3000);
    }
    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [scanId, isScanning]);


  const handleGenerate = async () => {
    if (!moduleData) return;
    setIsGenerating(true);
    setNapAuditRun(true);
    setListingsGenerated(false);
    
    try {
      const platformNames = moduleData.platforms.map(p => p.platform_name);
      const res = await profileListingService.generateListings({ platform_names: platformNames });
      setGenerationId(res.generation_id);
    } catch (err) {
      console.error('Error triggering generation', err);
      setIsGenerating(false);
    }
  };


  const napData = {
    name: moduleData?.project_context?.brand || '',
    address: moduleData?.project_context?.address || '',
    phone: moduleData?.project_context?.phone || '',
    website: moduleData?.project_context?.website_url || '',
    category: moduleData?.project_context?.primary_business || moduleData?.project_context?.keywords?.[0] || 'Business',
  };

  const platforms = moduleData?.platforms || [];

  const runNapAudit = async () => {
    setIsScanning(true);
    setNapAuditRun(false); // Reset before new scan
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        console.error('No user ID found for audit');
        setIsScanning(false);
        return;
      }
      const res = await profileListingService.initiateScan(userId, 'business_listing');
      setScanId(res.scan_id);
    } catch (err) {
      console.error('Failed to initiate scan', err);
      setIsScanning(false);
    }
  };
  
  const openDrawer = (listing: ProfileListingItem, mode: 'view' | 'edit') => {
    setDrawerListing(listing);
    setDrawerMode(mode);
    setEditDesc(listing.description || '');
    setEditBusinessName(listing.business_name || moduleData?.project_context?.brand || '');
    setEditAddress(listing.address || moduleData?.project_context?.address || '');
    setEditPhone(listing.phone || moduleData?.project_context?.phone || '');
    setEditWebsite(listing.website_url || moduleData?.project_context?.website_url || '');
    setEditCategory(listing.category || moduleData?.project_context?.primary_business || '');
  };

  const closeDrawer = () => {
    setDrawerListing(null);
  };

  const fallbackCopy = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try { document.execCommand("copy"); } catch (e) {}
    document.body.removeChild(textArea);
  };

  const handleSaveListing = async () => {
    if (!drawerListing) return;
    setIsSaving(true);
    try {
      const updated = await profileListingService.updateListing(drawerListing.id, {
        description: editDesc,
        business_name: editBusinessName,
        address: editAddress,
        phone: editPhone,
        website_url: editWebsite,
        category: editCategory
      });
      setGeneratedListings(prev => prev.map(l => l.id === updated.id ? updated : l));
      setDrawerListing(updated);
      setDrawerMode('view');
    } catch (err) {
      console.error("Failed to save listing", err);
    } finally {
      setIsSaving(false);
    }
  };


  if (loadingData) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back to Off-Page Content</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 border border-blue-200 dark:border-blue-800">
          <i className="ri-building-line text-blue-600 dark:text-blue-400 text-xl"></i>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Profile / Business Listing</h2>
            <button
              className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer group relative z-50"
              onClick={(e) => {
                e.stopPropagation();
                const t = document.getElementById('profile-business-tooltip');
                if (t) {
                  t.classList.toggle('opacity-0');
                  t.classList.toggle('opacity-100');
                  setTimeout(() => {
                    t.classList.add('opacity-0');
                    t.classList.remove('opacity-100');
                  }, 4000);
                }
              }}
            >
              <i className="ri-information-line text-lg"></i>
              <div
                id="profile-business-tooltip"
                className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left p-4 rounded-xl shadow-xl opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-blue-600 dark:text-blue-400"></i>
                  Prerequisite Inputs
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Full brand NAP (Name, Address, Phone)</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Logo & cover image</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Business category (must match GBP category)</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Operating hours & website URL</span>
                  </li>
                </ul>
              </div>
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Build and audit consistent NAP business profiles across top directories.</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-full p-1 relative">
        <button
          onClick={() => setActiveTab('new')}
          disabled={!moduleData?.is_new_website}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
            !moduleData?.is_new_website ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            activeTab === 'new'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <i className="ri-add-circle-line mr-1.5"></i>
          New Website
        </button>
        <button
          onClick={() => setActiveTab('existing')}
          disabled={moduleData?.is_new_website}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
            moduleData?.is_new_website ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            activeTab === 'existing'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <i className="ri-refresh-line mr-1.5"></i>
          Existing Website
        </button>
      </div>

      {/* NAP Card — Always Visible */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
          <i className="ri-profile-line text-blue-600 dark:text-blue-400"></i>
          Brand NAP (Name, Address, Phone)
          <div className="relative group cursor-pointer inline-flex items-center z-40">
            <i className="ri-question-line text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"></i>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-lg p-2.5 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity text-center font-normal leading-relaxed shadow-lg">
              You can enter or update these details in the Profile section.
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-200 dark:border-t-gray-700"></div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-white dark:border-t-gray-800" style={{ marginTop: '-1px' }}></div>
            </div>
          </div>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600">
            <span className="text-xs text-gray-400 dark:text-gray-500">Business Name</span>
            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{napData.name || 'Not provided'}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600">
            <span className="text-xs text-gray-400 dark:text-gray-500">Address</span>
            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{napData.address || 'Not provided'}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600">
            <span className="text-xs text-gray-400 dark:text-gray-500">Phone</span>
            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{napData.phone || 'Not provided'}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600">
            <span className="text-xs text-gray-400 dark:text-gray-500">Website</span>
            <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mt-1">{napData.website || 'Not provided'}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600">
            <span className="text-xs text-gray-400 dark:text-gray-500">Category</span>
            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{napData.category}</p>
          </div>
        </div>
      </div>

      {/* Directory Targets */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
          <i className="ri-global-line text-blue-600 dark:text-blue-400"></i>
          Target Directories
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {platforms.map((d) => (
            <div key={d.platform_name} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
              <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <i className={`ri-global-line text-blue-600 dark:text-blue-400`}></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{d.platform_name}</p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">DA {d.da_score}</span>
                </div>
              </div>
            </div>
          ))}
          {platforms.length === 0 && (
            <p className="text-sm text-gray-500 col-span-full">No platforms found for this module.</p>
          )}
        </div>
      </div>

      {/* Audit Section (Only for Existing Website) */}
      {activeTab === 'existing' && (
        <div className={`${cardBg} rounded-xl border ${borderColor} p-5 mb-5`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`text-sm font-semibold ${textPrimary}`}>NAP Consistency Audit</h3>
              <p className={`text-xs ${textSecondary} mt-0.5`}>
                Scan for existing business listings and audit NAP consistency.
              </p>
            </div>
            {!napAuditRun && !isScanning ? (
              <button
                onClick={runNapAudit}
                className="px-5 py-2.5 bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-eye-line mr-2"></i>
                Run NAP Audit
              </button>
            ) : isScanning ? (
              <button disabled className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Scanning...
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                  <i className="ri-check-line mr-1"></i>
                  Scan Completed
                </span>
                <button
                  onClick={runNapAudit}
                  className="px-4 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer bg-white dark:bg-gray-800 border border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <i className="ri-refresh-line mr-1"></i>Re-scan
                </button>
              </div>
            )}
          </div>

          {napAuditRun && (
            <div className="space-y-4">
              {scanAssets.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 mt-4 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-3">
                    <i className="ri-information-line text-blue-600 dark:text-blue-400 text-xl"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No Existing Listings Found</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-md">
                    We scanned the target directories but couldn't find any existing business profiles matching your brand and website.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Directory</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Field</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Current Value</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Expected Value</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Severity</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {scanAssets.map((issue, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{issue.directory}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{issue.field}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-red-600 dark:text-red-400 line-through">{issue.current}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-emerald-600 dark:text-emerald-400">{issue.expected}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${
                              issue.severity === 'critical'
                                ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'
                                : issue.severity === 'warning'
                                ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                                : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800'
                            }`}>{issue.severity}</span>
                          </td>
                          <td className="py-3 px-4">
                            <button className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors whitespace-nowrap cursor-pointer">
                              <i className="ri-edit-line mr-1"></i>
                              Fix
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Profile Generation & Asset Checklist Section */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5 space-y-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Generated Profiles</h3>
            <p className={`text-xs ${textSecondary} mt-0.5`}>
              Click a profile asset to view details and track submission checklists below
            </p>
          </div>
          
          {!isGenerating && !listingsGenerated ? (
            <button
              onClick={handleGenerate}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-magic-line mr-2"></i>
              Generate Profiles
            </button>
          ) : isGenerating ? (
            <button disabled className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Generating Profiles...
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                <i className="ri-check-line mr-1"></i>
                {generatedListings.length} Profiles Ready
              </span>
              <button
                onClick={handleGenerate}
                className="px-4 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer bg-white dark:bg-gray-800 border border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <i className="ri-refresh-line mr-1"></i>Regenerate
              </button>
            </div>
          )}
        </div>

        {isGenerating && !profileData && (
          <div className="flex flex-col items-center justify-center p-8 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400">AI is generating unique descriptions for target platforms...</p>
          </div>
        )}
        
        {profileData && (
          <div className="space-y-6">
            {/* Full-Width Asset Card */}
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-5 border border-gray-200 dark:border-gray-700 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h4 className="font-bold text-sm text-gray-900 dark:text-white flex items-center gap-2">
                  <i className="ri-file-list-3-line text-blue-500"></i>
                  Business Profile Details
                </h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fallbackCopy(profileData.description || '')}
                    className="px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 cursor-pointer flex items-center gap-1.5 transition-colors"
                  >
                    <i className="ri-file-copy-line" /> Copy Description
                  </button>
                  <button
                    onClick={() => openDrawer(profileData, 'edit')}
                    className="px-3 py-1.5 bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 cursor-pointer flex items-center gap-1.5 transition-colors"
                  >
                    <i className="ri-pencil-line" /> Edit Details
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Business Name</span>
                  <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{profileData.business_name || moduleData?.project_context?.brand || '—'}</p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Category</span>
                  <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{profileData.category || '—'}</p>
                </div>
                
                {profileData.description && (
                  <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 md:col-span-2">
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Description</span>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{profileData.description}</p>
                  </div>
                )}
                
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Phone</span>
                  <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{profileData.phone || moduleData?.project_context?.phone || '—'}</p>
                </div>
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Website</span>
                  <p className="font-semibold text-blue-600 dark:text-blue-400 text-sm">{profileData.website_url || moduleData?.project_context?.website_url || '—'}</p>
                </div>
                
                {profileData.address && (
                  <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 md:col-span-2">
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Address</span>
                    <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{profileData.address || moduleData?.project_context?.address || '—'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Submission Checklist Panel */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold text-sm text-gray-900 dark:text-white flex items-center gap-2">
                    <i className="ri-global-line text-blue-500" /> Target Directories Checklist
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Check off the directories as you submit this profile, then save your progress.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {markCompleteSuccess && (
                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                      <i className="ri-checkbox-circle-fill" /> Saved!
                    </span>
                  )}
                  <button
                    onClick={handleMarkCompletePerListing}
                    disabled={isMarkingComplete}
                    className="px-5 py-2.5 text-xs font-bold rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-sm cursor-pointer border-none flex items-center gap-1.5"
                  >
                    {isMarkingComplete ? (
                      <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                    ) : (
                      <><i className="ri-checkbox-circle-line" /> Save Progress</>
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-3 mt-4">
                {(moduleData?.platforms || [])
                  .slice()
                  .sort((a, b) => b.da_score - a.da_score)
                  .map((platform) => {
                  const isChecked = !!checkedPlatforms[platform.platform_name];

                  return (
                    <div
                      key={platform.platform_name}
                      className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                        isChecked
                          ? 'bg-emerald-50/30 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-900/50'
                          : 'bg-gray-50 dark:bg-gray-700/30 border-gray-100 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleTogglePlatformCheckbox(platform.platform_name)}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        />
                        <div className="w-10 h-10 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                          <i className={`ri-file-list-3-line ${isChecked ? 'text-emerald-500' : 'text-gray-400'} text-xl`}></i>
                        </div>
                        <div>
                          <span className={`text-sm font-bold block ${isChecked ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                            {platform.platform_name}
                          </span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                              DA {platform.da_score}
                            </span>
                            {isChecked && (
                              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                <i className="ri-check-line" /> Completed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <a
                        href={`https://${platform.platform_url || platform.platform_name.toLowerCase().replace(/\s+/g, '') + '.com'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300 hover:text-blue-600 hover:border-blue-300 dark:hover:text-blue-400 transition-colors rounded-lg flex items-center gap-1.5 font-semibold"
                      >
                        Visit Site <i className="ri-external-link-line" />
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail/Edit Drawer */}
      {drawerListing && (
        <>
          <div 
            className="fixed inset-0 bg-black/40 dark:bg-black/60 z-40 backdrop-blur-sm transition-opacity"
            onClick={closeDrawer}
          />
          <div className={`fixed inset-y-0 right-0 w-[500px] ${cardBg} shadow-2xl z-50 transform transition-transform duration-300 flex flex-col border-l ${borderColor}`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b ${borderColor} flex items-center justify-between`}>
              <div>
                <h2 className={`text-lg font-bold ${textPrimary}`}>
                  {drawerListing.platform_name} Profile
                </h2>
                <p className={`text-sm ${textSecondary}`}>
                  {drawerMode === 'view' ? 'Review generated profile details.' : 'Edit profile details before publishing.'}
                </p>
              </div>
              <button 
                onClick={closeDrawer}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 ${textSecondary} transition-colors cursor-pointer`}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {drawerMode === 'view' ? (
                <>
                  <div className="space-y-4">
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>Business Name</h4>
                      <p className={`text-sm ${textPrimary}`}>{drawerListing.business_name || moduleData?.project_context?.brand || <span className="text-gray-400 italic">Not set</span>}</p>
                    </div>
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>Description</h4>
                      <p className={`text-sm ${textPrimary} whitespace-pre-wrap leading-relaxed`}>{drawerListing.description}</p>
                    </div>
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>Category</h4>
                      <p className={`text-sm ${textPrimary}`}>{drawerListing.category || moduleData?.project_context?.primary_business || <span className="text-gray-400 italic">Not set</span>}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>Phone</h4>
                        <p className={`text-sm ${textPrimary}`}>{drawerListing.phone || moduleData?.project_context?.phone || <span className="text-gray-400 italic">Not set</span>}</p>
                      </div>
                      <div>
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>Website</h4>
                        <p className={`text-sm ${textPrimary} break-all`}>{drawerListing.website_url || moduleData?.project_context?.website_url || <span className="text-gray-400 italic">Not set</span>}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>Address</h4>
                      <p className={`text-sm ${textPrimary}`}>{drawerListing.address || moduleData?.project_context?.address || <span className="text-gray-400 italic">Not set</span>}</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>
                      Business Name
                    </label>
                    <input
                      type="text"
                      value={editBusinessName}
                      onChange={(e) => setEditBusinessName(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border ${borderColor} ${textPrimary} focus:ring-2 focus:ring-blue-500 outline-none transition-shadow text-sm`}
                      placeholder="Your Business Name"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>
                      Description (Keyword Optimised)
                    </label>
                    <textarea
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      rows={8}
                      className={`w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border ${borderColor} ${textPrimary} focus:ring-2 focus:ring-blue-500 outline-none transition-shadow text-sm resize-none`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>
                      Category
                    </label>
                    <input
                      type="text"
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border ${borderColor} ${textPrimary} focus:ring-2 focus:ring-blue-500 outline-none transition-shadow text-sm`}
                      placeholder="e.g. Digital Marketing Agency"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>
                        Phone
                      </label>
                      <input
                        type="text"
                        value={editPhone}
                        onChange={(e) => setEditPhone(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border ${borderColor} ${textPrimary} focus:ring-2 focus:ring-blue-500 outline-none transition-shadow text-sm`}
                        placeholder="+1 234 567 890"
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>
                        Website
                      </label>
                      <input
                        type="text"
                        value={editWebsite}
                        onChange={(e) => setEditWebsite(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border ${borderColor} ${textPrimary} focus:ring-2 focus:ring-blue-500 outline-none transition-shadow text-sm`}
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1`}>
                      Address
                    </label>
                    <input
                      type="text"
                      value={editAddress}
                      onChange={(e) => setEditAddress(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border ${borderColor} ${textPrimary} focus:ring-2 focus:ring-blue-500 outline-none transition-shadow text-sm`}
                      placeholder="123 Example Street..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`p-4 border-t ${borderColor} bg-gray-50 dark:bg-gray-800/50 flex items-center justify-end gap-3`}>
              {drawerMode === 'view' ? (
                <>
                  <button 
                    onClick={() => {
                      const text = `Business Name: ${drawerListing.business_name || moduleData?.project_context?.brand || ''}\nDescription: ${drawerListing.description || ''}\nCategory: ${drawerListing.category || moduleData?.project_context?.primary_business || ''}\nPhone: ${drawerListing.phone || moduleData?.project_context?.phone || ''}\nWebsite: ${drawerListing.website_url || moduleData?.project_context?.website_url || ''}\nAddress: ${drawerListing.address || moduleData?.project_context?.address || ''}`;
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
                      } else {
                        fallbackCopy(text);
                      }
                      setIsCopied(true);
                      setTimeout(() => setIsCopied(false), 2000);
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border ${borderColor} ${isCopied ? 'text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20' : textPrimary + ' hover:bg-gray-100 dark:hover:bg-gray-700'} transition-colors cursor-pointer w-[100px]`}
                  >
                    {isCopied ? (
                      <><i className="ri-check-line mr-2"></i>Copied!</>
                    ) : (
                      <><i className="ri-clipboard-line mr-2"></i>Copy</>
                    )}
                  </button>
                  <button 
                    onClick={() => setDrawerMode('edit')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border ${borderColor} ${textPrimary} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer`}
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Edit Content
                  </button>
                  <button 
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors flex items-center gap-2 cursor-pointer"
                    onClick={() => window.open(drawerListing.platform_url || `https://www.google.com/search?q=${drawerListing.platform_name}`, '_blank')}
                  >
                    <i className="ri-external-link-line"></i>
                    Open Platform
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => setDrawerMode('view')}
                    disabled={isSaving}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border ${borderColor} ${textPrimary} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50`}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSaveListing}
                    disabled={isSaving}
                    className="px-6 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-70"
                  >
                    {isSaving ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Saving...</>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
